import { useModal } from '@ebay/nice-modal-react';

import { useAuth } from '@zep/auth';
import { redirectSigninPage } from '@zep/common/routes';
import { SubscribePlan } from '@zep/types';

import { SelectMySpacesModal } from '../../subscription';
import { OnetimePeriod, PlanTemplateType, SubscriptionPeriod } from '../types';
import { generatePaymentQuery } from '../utils';

import { useMoveToPaymentWithQuery } from './useMoveToPaymentWithQuery';

/**
 * Pricing 페이지에서 시작하기 버튼이 눌렸을 때 사용되는 hook 입니다.
 * 결제 페이지로 가기 위한 쿼리를 생성하기까지 각종 로직을 처리합니다.
 * 일시납 배너를 눌렀을 때도 사용할 수 있습니다.
 */
export const useStartPlanPayment = () => {
  const { user, isInitialized } = useAuth();
  const { show: showSelectMySpacesModal, remove: removeSelectMySpacesModal } =
    useModal(SelectMySpacesModal);
  const moveToPayment = useMoveToPaymentWithQuery();

  const handleStartPlanPayment = async ({
    planType,
    planTemplateType,
    maxCcu,
    subscriptionPeriod,
    onetimePeriod,
  }: PlanPaymentParams) => {
    if (!isInitialized) {
      return;
    }
    if (!user) {
      return redirectSigninPage('/pricing');
    }

    await showSelectMySpacesModal({
      onSelectSpace: async space => {
        removeSelectMySpacesModal();

        moveToPayment(
          generatePaymentQuery(
            planTemplateType === PlanTemplateType.PRODUCT_ONETIME
              ? {
                  planType,
                  planTemplateType: PlanTemplateType.PRODUCT_ONETIME,
                  spaceHashId: space.hashId,
                  maxCcu,
                  onetimePeriod,
                }
              : {
                  planType,
                  planTemplateType: PlanTemplateType.PRODUCT_SUBSCRIPTION,
                  spaceHashId: space.hashId,
                  maxCcu,
                  subscriptionPeriod,
                },
          ),
        );
      },
    });
  };
  return { handleStartPlanPayment };
};

type PlanPaymentParams = Params;

type BaseParams = {
  planType: Omit<SubscribePlan, 'free'>;
  planTemplateType: PlanTemplateType;
  maxCcu?: number;
};

type SubscriptionParams = BaseParams & {
  planTemplateType:
    | PlanTemplateType.CUSTOM_SUBSCRIPTION
    | PlanTemplateType.PRODUCT_SUBSCRIPTION;
  subscriptionPeriod: SubscriptionPeriod;
  onetimePeriod?: never;
};

type OnetimeParams = BaseParams & {
  planTemplateType: PlanTemplateType.PRODUCT_ONETIME;
  onetimePeriod: OnetimePeriod;
  subscriptionPeriod?: never;
};

type Params = SubscriptionParams | OnetimeParams;
